import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import FriendsPanel from '../components/FriendsPanel';
import { ConfirmDialog } from '../components/Modals';
import { friendsApi, usersApi } from '../api/friendsApi';
import { useAuth } from '../auth/AuthContext';
import { DeckRow } from './DecksPage';
import type { DeckSummary, PublicProfile } from '../../../shared/types';

export default function ProfilePage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const { user, loading, getToken, refresh } = useAuth();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const load = useCallback(() => {
    if (loading) return;
    setError('');
    usersApi.profile(userId, getToken, refresh)
      .then(setProfile)
      .catch(e => setError(e instanceof Error ? e.message : 'Profile not found'));
    usersApi.decks(userId, getToken, refresh).then(setDecks).catch(() => setDecks([]));
  }, [userId, loading, getToken, refresh]);
  useEffect(() => { load(); }, [load]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); }
    finally { setBusy(false); load(); }
  }

  function friendButton() {
    if (!user || !profile || profile.friendship === 'self') return null;
    switch (profile.friendship) {
      case 'friends':
        return <button type="button" className="btn-danger" disabled={busy} onClick={() => setConfirmRemove(true)}>✓ Friends · Remove</button>;
      case 'incoming':
        return <button type="button" className="create-deck-btn" disabled={busy} onClick={() => act(() => friendsApi.accept(userId, getToken, refresh))}>Accept friend request</button>;
      case 'pending':
        return <button type="button" disabled>Request sent</button>;
      default:
        return <button type="button" className="create-deck-btn" disabled={busy} onClick={() => act(() => friendsApi.sendRequest({ userId }, getToken, refresh))}>+ Add friend</button>;
    }
  }

  return (
    <div>
      <SiteHeader />
      <main className="page page--wide">
        {error && <p className="error">{error}</p>}
        {profile && (
          <div className={`profile-layout${user ? '' : ' profile-layout--solo'}`}>
            <div className="profile-main">
              <div className="profile-head">
                <h1>{profile.displayName}</h1>
                {friendButton()}
              </div>
              <p className="muted"><Link to="/decks">← Decks</Link></p>
              <h2>Decks</h2>
              {!decks && <p className="muted">Loading…</p>}
              {decks && decks.length === 0 && <p className="muted">No visible decks.</p>}
              <div className="deck-list">{decks?.map(d => <DeckRow key={d.id} deck={d} />)}</div>
            </div>
            {user && (
              <aside className="profile-side">
                <h2>Friends</h2>
                <FriendsPanel onChanged={load} />
              </aside>
            )}
          </div>
        )}
        {!profile && !error && <p className="muted">Loading…</p>}
      </main>

      {confirmRemove && (
        <ConfirmDialog title={`Remove ${profile?.displayName ?? 'this friend'}?`} message="They'll be removed from your friends." confirmLabel="Remove" danger
          onConfirm={() => { setConfirmRemove(false); act(() => friendsApi.remove(userId, getToken, refresh)); }} onCancel={() => setConfirmRemove(false)} />
      )}
    </div>
  );
}
